import React, { useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  Dialog,
} from 'anf-core-react';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';
import Tmnt from '../../Tmnt/Tmnt';
import BagList from '../../Common/BagList/BagList';
import { GWP_BAG_MUTATION } from '../../../gql/shoppingBag.gql';
import BagContext from '../../../context/BagContext';
import useLog from '../../useLog/useLog';
import $window from '../../../tools/window';

export default function GiftWithPurchaseModal() {
  const { getSwitchValue } = useSwitchesFromContext();
  const hasGifWithPurchase = getSwitchValue('chkGiftWithPurchase');
  const isSaveForLaterEnabled = getSwitchValue('chkSaveForLater');
  const logger = useLog('shoppingBag.root');
  const {
    setBag,
    bag,
  } = useContext(BagContext);
  const { isSSR } = $window;
  const [isOpen, setIsOpen] = React.useState(false);
  const [processing, setProcessing] = React.useState(null);
  const { rewardId = '' } = bag?.freeGiftReward || {};

  const closeModal = () => {
    setIsOpen(false);
    // we need to make data-scroll-lock to false till DS team fix the Dialog issue from their end.
    document.documentElement.setAttribute('data-scroll-lock', 'false');
  };
  const openModal = () => { setIsOpen(true); };

  const [addGiftToBagMutation] = useMutation(GWP_BAG_MUTATION, {
    onCompleted: (data) => {
      const success = data?.addToCartGiftWithPurchase?.success;
      const statusCode = data?.addToCartGiftWithPurchase?.statusCode;
      if (success && statusCode === '200') {
        setBag((previousState) => {
          const newState = {
            ...previousState,
            bagItems: data?.addToCartGiftWithPurchase?.bagItems,
            orderTotals: data?.addToCartGiftWithPurchase?.orderTotals,
            promoInfo: data?.addToCartGiftWithPurchase?.promoInfo,
            repudiationData: data?.addToCartGiftWithPurchase?.repudiationData,
            freeShippingProgressInfo: data?.addToCartGiftWithPurchase?.freeShippingProgressInfo,
            freeGiftReward: data?.addToCartGiftWithPurchase?.freeGiftReward,
          };
          return newState;
        });
      } else {
        // handle error case
        setBag((previousState) => ({
          ...previousState,
          statusCode: data?.addToCartGiftWithPurchase?.statusCode,
          success: data?.addToCartGiftWithPurchase?.success,
          statusMessages: data?.addToCartGiftWithPurchase?.statusMessages,
        }));
      }
      setProcessing(false);
      closeModal();
    },
    onError: (error) => {
      logger.error('GWP_BAG_MUTATION failed', error);
      setProcessing(false);
      closeModal();
    },
  });

  const handleGiftSelection = (gift) => {
    if (!gift) return;
    setProcessing(gift?.shortSku);
    addGiftToBagMutation({
      variables: {
        rewardId,
        shortSku: gift?.shortSku || '',
        isSaveForLaterEnabled: false,
      },
    });
  };

  useEffect(() => {
    if (!bag?.freeGiftReward?.itemAdded && bag?.freeGiftReward?.isGiftRewardEligible) {
      openModal();
    }
  }, [bag]);

  if (
    !hasGifWithPurchase
    || bag?.freeGiftReward?.itemAdded
    || !bag?.freeGiftReward?.isGiftRewardEligible
    || isSSR
  ) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeModal}
      id="gift-with-purchase-modal"
      heading={(<h2><Tmnt tmnt={bag?.textFor?.gwpTitle} /></h2>)}
      closeButtonLabel="Close"
      classList="gift-with-purchase-modal"
    >
      <div className="gift-with-purchase-modal-content">
        <div data-testid="gift-with-purchase-modal-baglist" className="gift-with-purchase-modal-baglist">
          <BagList
            variant="giftWithPurchase"
            bagItems={bag?.freeGiftReward?.rewardItems?.items}
            tmntData={bag?.textFor}
            onGiftSelect={handleGiftSelection}
            processing={processing}
            isSaveForLaterEnabled={isSaveForLaterEnabled}
          />
        </div>
      </div>
    </Dialog>
  );
}
